import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Package, Image, FileText, Mail, MessageSquare, Camera, Play, MapPin, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { PackagesManager } from '../components/admin/PackagesManager';
import { CarouselManager } from '../components/admin/CarouselManager';
import { BlogManager } from '../components/admin/BlogManager';
import { NewsletterManager } from '../components/admin/NewsletterManager';
import { ContactManager } from '../components/admin/ContactManager';
import { GalleryManager } from '../components/admin/GalleryManager';
import { VideoManager } from '../components/admin/VideoManager';
import { TrailsManager } from '../components/admin/TrailsManager';
import { PageContentManager } from '../components/admin/PageContentManager';

export const AdminPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('packages');

  // Check authentication on mount
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('adminAuthenticated');
    if (!isAuthenticated || isAuthenticated !== 'true') {
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('adminUsername');
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    navigate('/login');
  };

  const username = localStorage.getItem('adminUsername');

  return (
    <section className="pt-24 pb-20 bg-gradient-to-br from-slate-50 to-emerald-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Panel</h1>
            <p className="text-slate-600">Manage your Move to Cornwall website</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm text-slate-600">Logged in as</p>
              <p className="font-semibold text-slate-900">{username}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-9 mb-8 bg-white border-2 border-emerald-200 p-1 h-auto">
            <TabsTrigger value="packages" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">Packages</span>
            </TabsTrigger>
            <TabsTrigger value="carousel" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <Image className="w-4 h-4" />
              <span className="hidden sm:inline">Carousel</span>
            </TabsTrigger>
            <TabsTrigger value="blog" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Blog</span>
            </TabsTrigger>
            <TabsTrigger value="galleries" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <Camera className="w-4 h-4" />
              <span className="hidden sm:inline">Galleries</span>
            </TabsTrigger>
            <TabsTrigger value="trails" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <MapPin className="w-4 h-4" />
              <span className="hidden sm:inline">Trails</span>
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <Play className="w-4 h-4" />
              <span className="hidden sm:inline">Videos</span>
            </TabsTrigger>
            <TabsTrigger value="pages" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <Layers className="w-4 h-4" />
              <span className="hidden sm:inline">Pages</span>
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Mailing</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center space-x-1 py-3 text-xs sm:text-sm">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Contact</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab Contents */}
          <TabsContent value="packages">
            <PackagesManager />
          </TabsContent>

          <TabsContent value="carousel">
            <CarouselManager />
          </TabsContent>

          <TabsContent value="blog">
            <BlogManager />
          </TabsContent>

          <TabsContent value="galleries">
            <GalleryManager />
          </TabsContent>

          <TabsContent value="trails">
            <TrailsManager />
          </TabsContent>

          <TabsContent value="videos">
            <VideoManager />
          </TabsContent>

          <TabsContent value="pages">
            <PageContentManager />
          </TabsContent>

          <TabsContent value="newsletter">
            <NewsletterManager />
          </TabsContent>

          <TabsContent value="contact">
            <ContactManager />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};
